import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, TrendingUp, Target, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCurvePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [scurveData, setScurveData] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const [scurveRes, projectRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/s-curve`),
        axios.get(`${API}/projects/${projectId}/kpis`)
      ]);
      setScurveData(scurveRes.data);
      setProjectInfo(projectRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform data for chart
  const chartData = scurveData?.months?.map(m => ({
    month: m.month,
    planned: m.planned_cum,
    actual: m.actual_cum,
    baseline: m.baseline_cum
  })) || [];

  // Find current month (last with actual data)
  const currentMonthIndex = chartData.findIndex(d => d.actual === null) - 1;
  const currentMonth = currentMonthIndex >= 0 ? chartData[currentMonthIndex] : chartData[chartData.length - 1];

  if (loading && !scurveData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="s-curve-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('scurve.title')}</h1>
            <p className="text-xs text-gray-500">{t('scurve.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Current Status Card */}
        {projectInfo && currentMonth && (
          <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium opacity-80 mb-3">{t('scurve.currentStatus')}</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs opacity-70">{t('common.planned')}</p>
                  <p className="text-2xl font-bold">{projectInfo.overall_progress_planned}%</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('common.actual')}</p>
                  <p className="text-2xl font-bold">{projectInfo.overall_progress_actual}%</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('common.variance')}</p>
                  <p className={`text-2xl font-bold ${projectInfo.progress_variance >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                    {projectInfo.progress_variance >= 0 ? '+' : ''}{projectInfo.progress_variance}%
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Calendar size={14} className="opacity-70" />
                <span className="text-xs opacity-70">
                  {projectInfo.start_date} → {projectInfo.end_date}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* S-Curve Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-600" />
              {t('scurve.chartTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ left: 0, right: 10 }}>
                  <defs>
                    <linearGradient id="colorPlanned" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 8 }} 
                    angle={-45} 
                    textAnchor="end" 
                    height={50}
                    interval={2}
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }} 
                    domain={[0, 100]}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-white p-3 shadow-lg rounded border text-xs">
                            <p className="font-semibold mb-2">{label}</p>
                            {payload.map((entry, index) => (
                              entry.value !== null && (
                                <p key={index} style={{ color: entry.color }}>
                                  {entry.name}: {entry.value?.toFixed(1)}%
                                </p>
                              )
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '10px' }} />
                  <ReferenceLine y={50} stroke="#9ca3af" strokeDasharray="3 3" />
                  <Area 
                    type="monotone" 
                    dataKey="baseline" 
                    name={t('scurve.baseline')} 
                    stroke="#9ca3af" 
                    fill="none"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="planned" 
                    name={t('common.planned')} 
                    stroke="#3b82f6" 
                    fill="url(#colorPlanned)"
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="actual" 
                    name={t('common.actual')} 
                    stroke="#22c55e" 
                    fill="url(#colorActual)"
                    strokeWidth={2}
                    connectNulls={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Legend Explanation */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-medium text-sm text-gray-900 mb-3">{t('scurve.understanding')}</h4>
            <div className="space-y-2 text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-blue-500"></div>
                <span><strong>{t('common.planned')}:</strong> {t('scurve.plannedDesc')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-green-500"></div>
                <span><strong>{t('common.actual')}:</strong> {t('scurve.actualDesc')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-gray-400" style={{borderStyle: 'dashed'}}></div>
                <span><strong>{t('scurve.baseline')}:</strong> {t('scurve.baselineDesc')}</span>
              </div>
              <p className="mt-2 text-gray-500">
                {t('scurve.gapExplanation')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Data Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('scurve.monthlyData')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-1">{t('scurve.month')}</th>
                    <th className="text-right py-2 px-1">{t('common.planned')}</th>
                    <th className="text-right py-2 px-1">{t('common.actual')}</th>
                    <th className="text-right py-2 px-1">{t('common.variance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.filter(d => d.actual !== null).slice(-6).map((row, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-1 font-medium">{row.month}</td>
                      <td className="text-right py-2 px-1 text-blue-600">{row.planned?.toFixed(1)}%</td>
                      <td className="text-right py-2 px-1 text-green-600">{row.actual?.toFixed(1)}%</td>
                      <td className={`text-right py-2 px-1 font-medium ${(row.actual - row.planned) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {((row.actual || 0) - (row.planned || 0)).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SCurvePage;
